import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import SeparatorWithText from '../common/SeperatorWithText';
import { Checkbox, message, Popover } from 'antd';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import WalkthroughTaskWithWorkOrderMessage from './WalkthroughTaskWithWorkOrderMessage';
import {
	canCheckTask,
	getPendingUnlinkedTaskItems,
	isTaskChecked,
} from '../../utils/TaskItemUtils';
import InlineHyperlink from '../common/InlineHyperlink';
import { withUserType } from '../withUserType';
import DateTimeHover from '../date_time_component/DateTime';
import { UserName } from '../name_component/user/UserNameComponent';
import { connect } from 'react-redux';
import moment from 'moment';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	walkthroughTaskItemsRestCrudThunksForBuyer,
	walkthroughTaskItemsRestCrudThunksForSupplier,
} from '../../thunks/walkthrough_task_items_thunks';
import { WALKTHROUGHS_CRUD_ACTION_CREATORS } from '../../actions/walkthroughs_actions';

const WalkthroughTaskListAnswerDisplay: FC<any> = ({
	walkthrough,
	taskAnswer,
	withTitle = false,
	hideDisabledHelper = false,
	includeOnlyPendingTaskItems = false,
	includeHelpText = false,
	userType,
	timeZoneId,
	currentUser,
	checkable = false,
	updateTask,
	updateWaklthroughPartially,
}) => {
	const [saving, setSaving] = useState(false);

	const taskListAnswer = useMemo(
		() =>
			includeOnlyPendingTaskItems
				? getPendingUnlinkedTaskItems(taskAnswer)
				: nullSafeGetOrElse('taskListAnswer', taskAnswer, []),
		[includeOnlyPendingTaskItems, taskAnswer]
	);

	const disabledContent: any = useCallback(
		(task) => {
			if (hideDisabledHelper) {
				return null;
			}
			const linkedWorkOrderId = nullSafeGet('linkedWorkOrderTaskItem.workOrderId', task);
			return linkedWorkOrderId ? (
				<WalkthroughTaskWithWorkOrderMessage linkedWorkOrderId={linkedWorkOrderId} />
			) : null;
		},
		[hideDisabledHelper]
	);

	const getLinkedWorkOrderLink = useCallback(
		(task) => {
			const linkedWorkOrderId = nullSafeGet('linkedWorkOrderTaskItem.workOrderId', task);
			return linkedWorkOrderId ? (
				<>
					<InlineHyperlink
						path={`/${userType}/workOrders/detail/${linkedWorkOrderId}/details`}
						text={`WO ${linkedWorkOrderId}`}
					/>{' '}
				</>
			) : null;
		},
		[userType]
	);

	const getAnsweredAtPopoverContent = useCallback(
		(walkthroughTask) =>
			walkthroughTask.answeredByContact && walkthroughTask.answeredAt ? (
				<div className="mt-2 flex flex-row items-center text-gray-400">
					<div className="mr-1">Completed by</div>
					<UserName contact={walkthroughTask.answeredByContact} mode={'inline'} placement={'top'} />
					<div className="mx-1">at</div>
					<DateTimeHover
						timestamp={walkthroughTask.answeredAt}
						showDate
						showTime
						timeZone={timeZoneId}
					/>
				</div>
			) : null,
		[timeZoneId]
	);

	const isTaskCheckable = useCallback(
		(walkthroughTask) => checkable && canCheckTask(walkthroughTask, currentUser),
		[checkable, currentUser]
	);

	const onCheck = useCallback(
		(task) => (e) => {
			stopDefaultEvents(e);

			const answer = nullSafeGetOrElse('answer', task, 'false') === 'false';
			setSaving(true);
			updateTask({
				...task,
				answer: `${answer}`,
				...(!!answer && { answeredBy: currentUser.email }),
				...(!!answer && { answeredAt: moment.utc() }),
			})
				.then((updatedTask) => {
					const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
					const updatedTaskAnswers = taskAnswers.map((ans) => {
						return ans.titleId === task.siteSurveyTaskTitleId
							? {
									...ans,
									taskListAnswer: nullSafeGetOrElse('taskListAnswer', ans, []).map((taskItem) => {
										return taskItem.id === updatedTask.id ? updatedTask : taskItem;
									}),
							  }
							: ans;
					});
					updateWaklthroughPartially({
						id: parseInt(walkthrough.id),
						taskAnswers: updatedTaskAnswers,
					});
				})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[updateTask, currentUser]
	);

	return (
		<div className="w-100 h5regular flex flex-col gap-3">
			{withTitle ? (
				<div className="mb-2 flex flex-col gap-2">
					<div className="flex flex-row items-baseline gap-2">
						<div className="h4medium">{`Task List (${taskListAnswer.length})`}</div>
						{includeHelpText ? (
							<div className="h5regular text-character-secondary">
								Completed tasks and tasks added to work orders are not included here.
							</div>
						) : null}
					</div>
					<SeparatorWithText />
				</div>
			) : null}
			{taskListAnswer.map((walkthroughTask) => {
				const disabledMessage = disabledContent(walkthroughTask);
				return (
					<div key={walkthroughTask.id} className="flex flex-row items-baseline">
						<Popover trigger="hover" content={disabledMessage}>
							<Checkbox
								disabled={!isTaskCheckable(walkthroughTask)}
								checked={isTaskChecked(walkthroughTask)}
								onClick={onCheck(walkthroughTask)}
							/>
						</Popover>
						<div
							className={`gap-2 pl-2 leading-6 ${
								isTaskChecked(walkthroughTask) ? 'line-through' : ''
							}`}
						>
							{getLinkedWorkOrderLink(walkthroughTask)}
							{nullSafeGetOrElse('title', walkthroughTask, '')}
							{walkthroughTask.answeredByContact ? (
								<>
									<span>{` --`}</span>
									{` `}
									<ContactAvatar
										size={24}
										contact={walkthroughTask.answeredByContact}
										popOverContent={getAnsweredAtPopoverContent(walkthroughTask)}
									/>
								</>
							) : null}
						</div>
					</div>
				);
			})}
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	walkthrough: state.walkthroughs.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateTask: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughTaskItemsRestCrudThunksForSupplier.update(entity)
				: walkthroughTaskItemsRestCrudThunksForBuyer.update(entity)
		),
	updateWaklthroughPartially: (entity) =>
		dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updatePartialSuccess(entity)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(WalkthroughTaskListAnswerDisplay as any) as any
);
